import React, { useState, useEffect, useRef } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import { Box } from "@mui/material";
import Header from "./components/Header";
import Footer from "./components/Footer";
import Home from "./components/Home";
import Products from "./components/Products";
import CategoryPage from "./components/CategoryPage";
import Checkout from "./components/Checkout";
import Account from "./components/Account";
import FAQ from "./components/FAQ";
import "./App.css";
import { CheckoutProvider } from "./components/CheckoutContext";
import Success from "./components/Success";
import Cancel from "./components/Cancel";
import ReturnPolicy from "./components/ReturnPolicy";
import About from "./components/About";
import Contact from "./components/Contact";
import Terms from "./components/Terms";
import PrivacyPolicy from "./components/Privacy";
import ProductDescription from "./components/ProductDescription";
import Sustainability from "./components/Sustainability";
import TrackOrder from "./components/TrackOrder";
import ReturnOrder from "./components/ReturnOrder";
import PaymentSuccessful from "./components/PaymentSuccessful";
import { DrawerProvider, useDrawer } from "./components/DrawerContext";
import { useCheckout } from "./components/CheckoutContext";
import CheckoutDrawer from "./components/CheckoutDrawer";
import CartDrawerContent from "./components/CartDrawerContent";
import Login from "./components/Login";
import Register from "./components/Register";
import Addresses from "./components/Addresses";
import { AuthProvider } from "./components/contexts/AuthContext";
import { PayPalScriptProvider } from "@paypal/react-paypal-js";
import ScrollToTop from "./components/ScrollToTop";
import Unsubscribe from "./components/Unsubscribe";
import Subscribe from "./components/Subscribe";

function AppContent() {
  const hoodieRef = useRef(null);
  const jewelleryRef = useRef(null);

  const { isDrawerOpen, closeDrawer } = useDrawer();

  const [userCountry, setUserCountry] = useState(null);
  
  const location = useLocation();
  const isAuthPage = location.pathname === "/login" || location.pathname === "/register";

  const supportedCountries = ["IN", "CA", "US", "GB", "AU", "EU"];
  const countryNames = {
    IN: "India",
    CA: "Canada",
    US: "United States",
    GB: "UK",
    AU: "Australia",
    EU: "Europe"
  };

  useEffect(() => {
    const detectCountry = async () => {
      const pathParts = location.pathname.split('/').filter(Boolean);
      const urlCountryCode = pathParts[0]?.toUpperCase();

      if (urlCountryCode && supportedCountries.includes(urlCountryCode)) {
        setUserCountry({ 
          code: urlCountryCode, 
          name: countryNames[urlCountryCode],
          originalCode: urlCountryCode 
        });
        return;
      }

      try {
        const response = await fetch("https://ipapi.co/json/");
        const data = await response.json();

        const euroCountries = ["AT", "BE", "HR", "CY", "EE", "FI", "FR", "DE", "GR", "IE", "IT", "LV", "LT", "LU", "MT", "NL", "PT", "SK", "SI", "ES"];
        let mappedCode = "default";
        
        if (data.country === "IN") mappedCode = "IN";
        else if (data.country === "CA") mappedCode = "CA";
        else if (data.country === "US") mappedCode = "US";
        else if (data.country === "GB") mappedCode = "GB";
        else if (data.country === "AU") mappedCode = "AU";
        else if (euroCountries.includes(data.country)) mappedCode = "EU";

        const country = { 
          code: mappedCode, 
          name: data.country_name || data.country, 
          originalCode: data.country 
        };

        setUserCountry(country);
      } catch (error) {
        console.error("Failed to detect country:", error);
        setUserCountry({ code: "US", name: "United States" });
      }
    };

    detectCountry();
  }, [location.pathname]);

  // --- Auto-Apply Discount Code ---
  const { dispatch: checkoutDispatch } = useCheckout();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const coupon = params.get("coupon");
    if (coupon) {
      const normalizedCoupon = coupon.toUpperCase();
      checkoutDispatch({ type: "SET_COUPON", payload: normalizedCoupon });
      
      // Optionally remove from URL to keep it clean
      const newUrl = window.location.pathname + window.location.search.replace(/[?&]coupon=[^&]+/, '').replace(/^&/, '?');
      window.history.replaceState({}, '', newUrl);
    }
  }, [location.search, checkoutDispatch]);

  const homeElement = (
    <>
      <Home hoodieRef={hoodieRef} />
      <Products
        hoodieRef={hoodieRef}
        jewelleryRef={jewelleryRef}
        userCountry={userCountry}
      />
    </>
  );

  return (
    <div className="App" style={{ maxWidth: "100%", overflowX: "hidden", backgroundColor: isAuthPage ? "#f4f4f4" : "transparent", minHeight: "100vh", display: 'flex', flexDirection: 'column' }}>
      <ScrollToTop />
      {!isAuthPage && (
        <Header
          hoodieRef={hoodieRef}
          jewelleryRef={jewelleryRef}
          userCountry={userCountry}
        />
      )}

      <Box sx={{ flexGrow: 1 }}>
        <Routes>
        {/* HOME */}
        <Route path="/" element={homeElement} />
        {supportedCountries.map(code => (
          <Route key={code} path={`/${code.toLowerCase()}`} element={homeElement} />
        ))}

        {/* 🔥 CATEGORY ROUTES */}
        <Route
          path="/hoodies"
          element={
            <Products
              hoodieRef={hoodieRef}
              userCountry={userCountry}
              showOnlyHoodies={true}
            />
          }
        />

        <Route
          path="/sweatshirts"
          element={
            <Products
              hoodieRef={hoodieRef}
              userCountry={userCountry}
              showOnlyHoodies={true}
            />
          }
        />

        <Route
          path="/t-shirts"
          element={
            <Products
              hoodieRef={hoodieRef}
              userCountry={userCountry}
              showOnlyTshirts={true}
            />
          }
        />

        <Route
          path="/shorts"
          element={
            <Products
              hoodieRef={hoodieRef}
              userCountry={userCountry}
              showOnlyShorts={true}
            />
          }
        />

        {/* CATEGORY ROUTES */}
        <Route
          path="/products/:category"
          element={<CategoryPage userCountry={userCountry} />}
        />

        {/* 🔥 SHOP ROUTES */}
        <Route
          path="/all-products"
          element={<CategoryPage userCountry={userCountry} isAllProducts />}
        />
        <Route
          path="/search"
          element={<CategoryPage userCountry={userCountry} isSearch />}
        />

        {/* OTHER ROUTES */}
        <Route
          path="/checkout/:token"
          element={<Checkout userCountry={userCountry} />}
        />
        <Route path="/account" element={<Account />} />
        <Route path="/account/addresses" element={<Addresses />} />
        <Route path="/faq" element={<FAQ />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route
          path="/product-description/:productSlug"
          element={<ProductDescription userCountry={userCountry} />}
        />
        <Route path="/contact" element={<Contact />} />
        <Route path="/return-policy" element={<ReturnPolicy />} />
        <Route path="/about" element={<About />} />
        <Route path="/success" element={<Success />} />
        <Route path="/cancel" element={<Cancel />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/sustainability" element={<Sustainability />} />
        <Route path="/paymentSuccessful" element={<PaymentSuccessful />} />
        <Route path="/track-order" element={<TrackOrder />} />
        <Route path="/return-order" element={<ReturnOrder />} />
        <Route path="/account/unsubscribe" element={<Unsubscribe />} />
        <Route path="/account/subscribe" element={<Subscribe />} />
      </Routes>
      </Box>

      {!isAuthPage && <Footer />}

      {!isAuthPage && (
        <CheckoutDrawer open={isDrawerOpen} onClose={closeDrawer}>
          <CartDrawerContent closeDrawer={closeDrawer} userCountry={userCountry} />
        </CheckoutDrawer>
      )}

    </div>
  );
}

function App() {
  return (
    <PayPalScriptProvider
      options={{
        "client-id":
          "AZOdF3i4r_oDBgmhMsADpERpD9ATCPxnn-1-xdQrnjGTk02dqN0ajn8KaDIH6xRR5HreotgXToUGJQXp",
        currency: "USD",
      }}
    >
      <CheckoutProvider>
        <DrawerProvider>
          <AuthProvider>
            <AppContent />
          </AuthProvider>
        </DrawerProvider>
      </CheckoutProvider>
    </PayPalScriptProvider>
  );
}

export default App;
